import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { isApiConfigured, isGoogleOAuthConfigured } from "../auth-config";
import { useAuth } from "../auth/AuthContext";
import {
  resolvePostAuthRoute,
  sanitizeReturnTo,
  savePostLoginReturnTo,
} from "../auth/post-login-redirect";
import { parseSignupIntentParam, saveSignupIntent } from "../auth/signup-intent";
import { LEGAL_SUPPORT_EMAIL } from "./legal/legal-meta";
import SiteFooter from "./legal/SiteFooter";

export default function LoginPage() {
  const { status, profile, beginGoogleSignIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = sanitizeReturnTo(searchParams.get("returnTo"));
  const signupIntent = parseSignupIntentParam(searchParams.get("intent"));
  const googleIdConfigured = isGoogleOAuthConfigured();
  const apiConfigured = isApiConfigured();
  const canSignIn = googleIdConfigured && apiConfigured;

  useEffect(() => {
    if (returnTo) savePostLoginReturnTo(returnTo);
    if (signupIntent) saveSignupIntent(signupIntent);
  }, [returnTo, signupIntent]);

  const loginLede =
    signupIntent === "teacher"
      ? "Use the Google account you use at school. Next, you will start your free teacher trial."
      : signupIntent === "student" || returnTo?.startsWith("/join")
        ? "Use the Google account you use at school. Next, you will join your class with a join code."
        : "Use the Google account you use at school. The first time you visit, we will ask what you are here to do.";

  if (status === "authenticated" && profile) {
    return <Navigate to={resolvePostAuthRoute(profile)} replace />;
  }

  return (
    <main className="page" data-google-oauth={googleIdConfigured ? "configured" : "pending"}>
      <div className="card">
        <p className="eyebrow">MsLeonard.com</p>
        <h1>Sign in</h1>
        <p className="lede">{loginLede}</p>

        {!googleIdConfigured ? (
          <p className="warn-banner">
            {import.meta.env.DEV ? (
              <>
                OAuth is not configured for this build. Set{" "}
                <code className="inline-code">VITE_GOOGLE_CLIENT_ID</code> (see{" "}
                <code className="inline-code">apps/web/.env.example</code>).
              </>
            ) : (
              <>
                Sign-in isn&apos;t set up on this page yet. Ask your teacher, or email{" "}
                <a href={`mailto:${LEGAL_SUPPORT_EMAIL}`}>{LEGAL_SUPPORT_EMAIL}</a>.
              </>
            )}
          </p>
        ) : null}

        {googleIdConfigured && !apiConfigured ? (
          <p className="warn-banner">
            {import.meta.env.DEV ? (
              <>
                This build has no HTTP API base URL (<code className="inline-code">VITE_API_BASE_URL</code>
                ). Production should resolve <code className="inline-code">PublicApiUrl</code> when
                deploying, or set GitHub Variable <code className="inline-code">CCS_API_URL</code>. Locally,
                copy <code className="inline-code">apps/web/.env.example</code> to{" "}
                <code className="inline-code">.env.local</code> and set the API URL. Google Web OAuth
                cannot finish in the browser alone.
              </>
            ) : (
              <>
                Sign-in isn&apos;t working right now. Ask your teacher, or email{" "}
                <a href={`mailto:${LEGAL_SUPPORT_EMAIL}`}>{LEGAL_SUPPORT_EMAIL}</a>.
              </>
            )}
          </p>
        ) : null}

        <div className="actions">
          <button
            type="button"
            className="btn primary"
            disabled={!canSignIn}
            onClick={() => {
              if (returnTo) savePostLoginReturnTo(returnTo);
              if (signupIntent) saveSignupIntent(signupIntent);
              void beginGoogleSignIn();
            }}
          >
            Continue with Google
          </button>
          <button type="button" className="btn secondary" onClick={() => navigate("/", { replace: true })}>
            Back home
          </button>
        </div>

        <p className="fineprint">
          We use your name and school email from Google so we know it&apos;s really you. By continuing
          you agree to our <Link to="/terms">Terms of Service</Link> and{" "}
          <Link to="/privacy">Privacy Policy</Link>.
        </p>

        <SiteFooter />
      </div>
    </main>
  );
}
